import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, QrCode, RefreshCw, Save, XCircle } from "lucide-react";
import { formatTry, siteAddonsSchema, type SiteAddons, type WeddingAddonStatus } from "@wedding/shared";
import { adminAddonsApi, adminSiteApi, type AdminAddonRow } from "../lib/api";
import { AppLink } from "../lib/route-primitives";
import { paths } from "../lib/paths";
import { cn } from "../lib/utils";

/**
 * "QR ile ekstra kayıt" paketleri (2026-10-15).
 * - `AddonPricingCard` (Fiyatlar sekmesi): paket kişi sayısı + fiyatı, aç/kapa → `site:addons`.
 * - `AddonsPanel` (Online satış sekmesi): organizatörlerin açtığı paketler — açılınca
 *   hemen aktif, burada havale görülünce "Ödendi", ödenmezse "İptal" (koltuklar düşer).
 */

type Draft = { enabled: boolean; packs: { seats: string; price: string }[] };
const toDraft = (a: SiteAddons): Draft => ({
  enabled: a.qrExtra.enabled,
  packs: a.qrExtra.packs.map((p) => ({ seats: String(p.seats), price: String(p.price) })),
});
const num = (v: string) => (/^\d+$/.test(v.replace(/\./g, "")) ? Number(v.replace(/\./g, "")) : NaN);

export function AddonPricingCard({ saved, onSaved, showToast }: {
  saved: SiteAddons;
  onSaved: (a: SiteAddons) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => toDraft(saved));
  const [saving, setSaving] = useState(false);
  const parsed = siteAddonsSchema.safeParse({
    qrExtra: { enabled: draft.enabled, packs: draft.packs.map((p) => ({ seats: num(p.seats), price: num(p.price) })) },
  });
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(saved));

  const setPack = (i: number, patch: Partial<Draft["packs"][number]>) =>
    setDraft((d) => ({ ...d, packs: d.packs.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));

  const save = async () => {
    if (!parsed.success) {
      showToast(t("siteAdmin.invalid"), false);
      return;
    }
    setSaving(true);
    try {
      const res = await adminSiteApi.put("addons", parsed.data);
      onSaved(res.data ?? parsed.data);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      showToast(t("siteAdmin.saveFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="addon-pricing">
      <div className="px-5 py-4 border-b border-border bg-muted/30 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <QrCode className="h-4 w-4 text-primary" aria-hidden="true" />
            <span>{t("siteAdmin.addons.title")}</span>
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">{t("siteAdmin.addons.desc")}</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
            className="h-4 w-4 accent-primary cursor-pointer"
          />
          <span>{t("siteAdmin.addons.enabled")}</span>
        </label>
      </div>
      <ul className="divide-y divide-border">
        {draft.packs.map((p, i) => (
          <li key={i} className="grid grid-cols-2 sm:grid-cols-[8rem_10rem_minmax(0,1fr)] items-center gap-3 px-5 py-3">
            <label className="text-xs text-muted-foreground">
              <span className="block mb-1">{t("siteAdmin.addons.seats")}</span>
              <input
                inputMode="numeric"
                value={p.seats}
                onChange={(e) => setPack(i, { seats: e.target.value })}
                aria-label={t("siteAdmin.addons.seatsFor", { n: i + 1 })}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <label className="text-xs text-muted-foreground">
              <span className="block mb-1">{t("siteAdmin.addons.price")}</span>
              <input
                inputMode="numeric"
                value={p.price}
                onChange={(e) => setPack(i, { price: e.target.value })}
                aria-label={t("siteAdmin.addons.priceFor", { n: i + 1 })}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <p className="col-span-2 sm:col-span-1 text-xs text-muted-foreground tabular-nums">
              {Number.isFinite(num(p.seats)) && Number.isFinite(num(p.price)) && num(p.seats) > 0
                ? t("siteAdmin.addons.perGuest", { price: formatTry(Math.round(num(p.price) / num(p.seats)), i18n.language) })
                : "—"}
            </p>
          </li>
        ))}
      </ul>
      <div className="px-5 py-3 flex items-center justify-between gap-3 border-t border-border">
        {!parsed.success ? (
          <p className="text-xs text-red-500">{t("siteAdmin.addons.invalid")}</p>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !dirty || !parsed.success}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
          <span>{t("siteAdmin.addons.save")}</span>
        </button>
      </div>
    </div>
  );
}

const FILTERS: (WeddingAddonStatus | "all")[] = ["pending", "paid", "cancelled", "all"];

export function AddonsPanel({ showToast }: { showToast: (msg: string, ok?: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<WeddingAddonStatus | "all">("pending");
  const [items, setItems] = useState<AdminAddonRow[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await adminAddonsApi.list({ status: filter === "all" ? undefined : filter });
      setAvailable(res.data?.available ?? true);
      setItems(res.data?.items ?? []);
    } catch (err) {
      setItems([]);
      showToast(t("siteAdmin.addons.loadFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    }
  }, [filter, showToast, t]);
  useEffect(() => {
    void load();
  }, [load]);

  const act = async (a: AdminAddonRow, status: "paid" | "cancelled") => {
    // Tutarlı paketi "ödendi" işaretlemek = tahsilat → ödeme referansı (dekont / havale açıklaması)
    // zorunlu; sunucu da denetler (400 payment_ref_required) ve deftere yazar.
    let adminNote: string | undefined;
    if (status === "paid" && a.amount_try > 0 && !a.admin_note?.trim()) {
      const ref = window.prompt(t("siteAdmin.addons.paidRefPrompt", { seats: a.seats, slug: a.wedding_slug, amount: formatTry(a.amount_try, i18n.language) }));
      if (ref === null) return;
      if (!ref.trim()) {
        showToast(t("siteAdmin.addons.paymentRefRequired"), false);
        return;
      }
      adminNote = ref.trim();
    } else {
      const key = status === "paid" ? "siteAdmin.addons.paidConfirm" : "siteAdmin.addons.cancelConfirm";
      if (!window.confirm(t(key, { seats: a.seats, slug: a.wedding_slug }))) return;
    }
    setBusy(a.id);
    try {
      await adminAddonsApi.patch(a.id, adminNote ? { status, adminNote } : { status });
      showToast(t(status === "paid" ? "siteAdmin.addons.markedPaid" : "siteAdmin.addons.cancelled"));
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(msg === "payment_ref_required" ? t("siteAdmin.addons.paymentRefRequired") : t("siteAdmin.saveFailed", { error: msg }), false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="addons-panel">
      <div className="px-5 py-4 border-b border-border bg-muted/30 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{t("siteAdmin.addons.panelTitle")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.addons.panelDesc")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                filter === f ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`siteAdmin.addons.filter.${f}`)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void load()}
            aria-label={t("siteAdmin.reload")}
            className="p-2 rounded-full border border-border text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="px-5 py-4">
        {!available ? (
          <p className="text-sm text-amber-700 dark:text-amber-300" data-testid="addons-unavailable">{t("siteAdmin.addons.unavailable")}</p>
        ) : items === null ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("siteAdmin.addons.empty")}</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="addons-list">
            {items.map((a) => (
              <li key={a.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-foreground">
                    <AppLink to={paths.wedding.dashboard(a.wedding_slug)} className="hover:underline cursor-pointer">
                      {a.wedding_title}
                    </AppLink>{" "}
                    <span className="text-muted-foreground">/{a.wedding_slug}</span>
                  </p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    +{a.seats} · {formatTry(a.amount_try, i18n.language)} · {t(`siteAdmin.addons.filter.${a.status}`)} · {a.created_by} · {a.created_at}
                  </p>
                  {a.admin_note && <p className="text-xs text-muted-foreground" data-testid="addon-note">{a.admin_note}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  {a.status === "pending" && (
                    <button
                      type="button"
                      onClick={() => void act(a, "paid")}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-40 cursor-pointer"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                      <span>{t("siteAdmin.addons.markPaid")}</span>
                    </button>
                  )}
                  {a.status !== "cancelled" && (
                    <button
                      type="button"
                      onClick={() => void act(a, "cancelled")}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-red-500/5 hover:text-red-600 disabled:opacity-40 cursor-pointer"
                    >
                      <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                      <span>{t("siteAdmin.addons.cancel")}</span>
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
